import * as tslib_1 from "tslib";
import closeCompose from './closeCompose';
import floatCompose from './floatCompose';
import { messageBeingSent } from './messageBeingSent';
import onSuccessfulSendFromInlineCompose from './onSuccessfulSendFromInlineCompose';
import removeInfoBarMessage from './removeInfoBarMessage';
import setIsSending from './setIsSending';
import setAsyncSendState from '../actions/setAsyncSendState';
import { waitForActiveSaving } from '../actions/trySaveMessage';
import datapoints from '../datapoints';
import { findComposePlugins } from '../plugins/ComposePlugins';
import composeStore from '../store/composeStore';
import getFriendlySubject from '../utils/getFriendlySubject';
import { sendAndSaveInfobarIdsToRemove } from '../utils/InfoBarMessageId';
import shouldUseAsyncSend from '../utils/shouldUseAsyncSend';
import validateSend from '../utils/validateSend';
import { returnTopExecutingActionDatapoint } from 'owa-analytics';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import endSession from 'owa-controls-findpeople-feedback-manager/lib/actions/endSession';
import { addMilliseconds, now } from 'owa-datetime';
import { getDelayedItemStore, lazyRemoveMailItemWithData } from 'owa-mail-delaysend-store';
import { isMailFeatureEnabled } from 'owa-mail-feature-flags';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import strings from 'owa-strings/owa-mail-strings';
import { closeAllSxS } from 'owa-sxs-store';
import { findTabByData, lazyActivateTab } from 'owa-tab-store';
import { action } from 'satcheljs/lib/legacy';
import { sendMessage, handleSuccessResponse, handleErrorResponse, } from '../utils/sendSaveUtils';
import { addNotification, ensureNotificationHandlerRegistered, NotificationActionType, } from 'owa-header-toast-notification';
import { logSmartReplyDatapoint } from '../utils/logSmartReplyComposeDatapoint';
function asyncSend(viewState, asyncSendDataPoint) {
    return tslib_1.__awaiter(this, void 0, void 0, function () {
        var _this = this;
        var sendMessagePromise, delaySendInterval;
        return tslib_1.__generator(this, function (_a) {
            sendMessagePromise = null;
            delaySendInterval = getUserConfiguration().UserOptions.MailSendUndoInterval
                ? getUserConfiguration().UserOptions.MailSendUndoInterval * 1000 //MailSendUndoInterval interval is expressed in seconds
                : 0;
            if (isMailFeatureEnabled('cmp-delaySend') && delaySendInterval > 0) {
                getDelayedItemStore().delayedMailItems.push({
                    timeoutId: window.setTimeout(function () {
                        // First, clear the delayed item from the store (as it's sending now)
                        lazyRemoveMailItemWithData.importAndExecute(viewState);
                        sendMessage(viewState)
                            .then(function (responseMessage) {
                            // Handle the response here to avoid false alerm of sending failure caused by client side error
                            setAsyncSendState(viewState, 4 /* Sent */);
                            return Promise.resolve(responseMessage);
                        })
                            .catch(function (error) {
                            // Only show the send failure notification when we really got server side sending error
                            handleAsyncSendError(viewState);
                            error.showErrorToUser = true;
                            throw error;
                        });
                    }, delaySendInterval),
                    timeToSend: addMilliseconds(now(), delaySendInterval),
                    data: viewState,
                    afterCancel: function (data) { return setAsyncSendState(data, 0 /* None */); },
                });
                sendMessagePromise = Promise.resolve();
            }
            else {
                sendMessagePromise = sendMessage(viewState)
                    .then(function (responseMessage) {
                    // Handle the response here to avoid false alerm of sending failure caused by client side error
                    setAsyncSendState(viewState, 4 /* Sent */);
                    return Promise.resolve(responseMessage);
                })
                    .catch(function (error) {
                    // Only show the send failure notification when we really got server side sending error
                    handleAsyncSendError(viewState);
                    error.showErrorToUser = true;
                    throw error;
                });
            }
            setAsyncSendState(viewState, 1 /* Sending */);
            if (viewState.isInlineCompose) {
                onSuccessfulSendFromInlineCompose(viewState, true /*markAsPending*/);
            }
            else {
                closeAllSxS('sendMessage');
            }
            // Set to false in case the send is failed, then the retry will use sync send
            viewState.preferAsyncSend = false;
            // Run this in requestAnimationFrame to provide a chance for react to rerender the view,
            // then we log the datapoint which will contain the rerender time.
            window.requestAnimationFrame(function () { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                return tslib_1.__generator(this, function (_a) {
                    if (asyncSendDataPoint) {
                        asyncSendDataPoint.end();
                    }
                    return [2 /*return*/];
                });
            }); });
            return [2 /*return*/, sendMessagePromise];
        });
    });
}
function syncSend(viewState) {
    return tslib_1.__awaiter(this, void 0, void 0, function () {
        var responseMessage;
        return tslib_1.__generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, sendMessage(viewState)];
                case 1:
                    responseMessage = _a.sent();
                    endSession(viewState.findPeopleFeedbackManager, 'Send');
                    closeCompose(viewState);
                    if (viewState.isInlineCompose) {
                        onSuccessfulSendFromInlineCompose(viewState, true /*markAsPending*/);
                    }
                    return [2 /*return*/, Promise.resolve(responseMessage)];
            }
        });
    });
}
function onAsyncSendNotificationClick(notification) {
    var viewState = notification.actionContext;
    var tab = findTabByData(viewState);
    if (tab) {
        lazyActivateTab.importAndExecute(tab);
    }
    else {
        floatCompose(composeStore.pendingViewState, 0 /* Normal */);
    }
}
function addErrorNotification(viewState) {
    var subject = getFriendlySubject(viewState.subject);
    ensureNotificationHandlerRegistered(NotificationActionType.AsyncSendFailed, onAsyncSendNotificationClick);
    var notification = {
        actionType: NotificationActionType.AsyncSendFailed,
        title: strings.failToSendMessageError,
        description: subject,
        actionLink: strings.moreDetailsNotificationLabel,
        cancelLink: null,
        actionContext: viewState,
    };
    addNotification(notification);
}
function handleAsyncSendError(viewState) {
    if (viewState.operation == 1 /* Reply */ ||
        viewState.operation == 2 /* ReplyAll */) {
        onSuccessfulSendFromInlineCompose(viewState, false /*markAsPending*/);
    }
    // Only do this in Sending state because Sending means the viewState is still pending,
    // while it will be Timeout state after a period, in that case the viewState is floating rather than pending/
    // We don't need to show error notification for floating compose because the form itself is already visible
    if (viewState.asyncSendState == 1 /* Sending */) {
        addErrorNotification(viewState);
    }
    if (viewState.asyncSendState != 0 /* None */) {
        setAsyncSendState(viewState, 2 /* Error */);
    }
}
function endCtq(datapoint, error) {
    window.requestAnimationFrame(function () {
        if (error) {
            datapoint.addCustomData(datapoints.MailComposeSend.datapoint.customData(error.showErrorToUser, error.isAsyncSend, error.messageText));
            datapoint.endWithError(DatapointStatus.ServerError, error);
        }
        else {
            datapoint.end();
        }
    });
}
export default action('trySendMessage', datapoints.MailComposeSendE2E)(function trySendMessage(viewState) {
    return tslib_1.__awaiter(this, void 0, void 0, function () {
        var mailComposeSendE2EDataPoint, asyncSendDatapoint, errorCode, sendDatapoint_1, useAsyncSend_1, sendPromise;
        return tslib_1.__generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (viewState.isSending) {
                        // Avoid duplicated sending
                        return [2 /*return*/, Promise.resolve()];
                    }
                    mailComposeSendE2EDataPoint = returnTopExecutingActionDatapoint();
                    asyncSendDatapoint = new PerformanceDatapoint(datapoints.MailComposeSendAsync.datapoint.name);
                    removeInfoBarMessage(viewState, sendAndSaveInfobarIdsToRemove);
                    setIsSending(viewState, true /*isSending*/);
                    return [4 /*yield*/, validateSend(viewState, mailComposeSendE2EDataPoint)];
                case 1:
                    errorCode = _a.sent();
                    if (!!errorCode) return [3 /*break*/, 4];
                    return [4 /*yield*/, waitForActiveSaving(viewState)];
                case 2:
                    _a.sent();
                    messageBeingSent();
                    sendDatapoint_1 = new PerformanceDatapoint(datapoints.MailComposeSend.datapoint.name);
                    useAsyncSend_1 = shouldUseAsyncSend(viewState);
                    sendPromise = useAsyncSend_1
                        ? asyncSend(viewState, asyncSendDatapoint)
                        : syncSend(viewState);
                    return [4 /*yield*/, sendPromise
                            .then(function (responseMessage) {
                            // contentOperationPlugin will do auto save when dispose. Since we have already sent the message, we need to stop this action
                            var plugins = findComposePlugins(viewState);
                            if (plugins && plugins.contentOperationPlugin) {
                                plugins.contentOperationPlugin.stopSaveOnDispose();
                            }
                            // If we are sending a message created via smart reply, log a datapoint.
                            logSmartReplyDatapoint(0 /* Send */, viewState.smartReplyExtractionId);
                            setIsSending(viewState, false /*isSending*/);
                            endCtq(sendDatapoint_1);
                            handleSuccessResponse(responseMessage, viewState);
                            return Promise.resolve();
                        })
                            .catch(function (error) {
                            error.isAsyncSend = useAsyncSend_1;
                            endCtq(sendDatapoint_1, error);
                            setIsSending(viewState, false /*isSending*/);
                            handleErrorResponse(error, viewState, true /*isSend*/);
                            return Promise.reject(error);
                        })];
                case 3:
                    _a.sent();
                    return [3 /*break*/, 5];
                case 4:
                    setIsSending(viewState, false /*isSending*/);
                    return [2 /*return*/, Promise.reject(new Error(errorCode))];
                case 5: return [2 /*return*/];
            }
        });
    });
});
//# sourceMappingURL=trySendMessage.js.map